import type { BookingOption, HotelBootstrap } from "../contract";

/**
 * Placeholder imagery as inline SVG.
 *
 * Data URIs rather than files so nothing depends on a host, a CDN or the hotel page's own
 * content-security-policy — an embed that loses its images behind a strict CSP looks broken and
 * would be blamed on us. Real hotels supply real URLs through the same field.
 */
function placeholder(from: string, to: string): string {
  // No caption: the card names the room right underneath, and `object-fit: cover` would crop
  // any text placed here anyway.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 480 300">
    <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${from}"/><stop offset="1" stop-color="${to}"/>
    </linearGradient></defs>
    <rect width="480" height="300" fill="url(#g)"/>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Three hotels with deliberately different rule profiles.
 *
 * They exist to make the flow's adaptivity falsifiable: if the stepper looks the same for a
 * one-category room-only guesthouse and a full-board resort, the adaptivity is decoration. Each
 * profile also carries a branding case worth looking at — a safe accent, an accent too light to
 * put white text on, and a dark-mode house style.
 */

function preference(code: string, displayName: string, description: string | null): BookingOption {
  return {
    code,
    displayName,
    description,
    scope: "room",
    kind: "preference",
    // A preference is never sold. Anything the hotel can actually promise belongs in a
    // category's guaranteedAttributes or in requirements instead.
    guaranteeLevel: "requested",
    priceStatus: "free",
    availabilityStatus: "available",
    price: null,
    pricingUnit: null,
    conflictsWith: [],
    requires: [],
    maxQuantity: null,
  };
}

function addon(
  code: string,
  displayName: string,
  description: string | null,
  formatted: string,
  pricingUnit: BookingOption["pricingUnit"],
  scope: BookingOption["scope"] = "booking",
  overrides: Partial<BookingOption> = {},
): BookingOption {
  return {
    code,
    displayName,
    description,
    scope,
    kind: "addon",
    guaranteeLevel: "guaranteed",
    priceStatus: "priced",
    availabilityStatus: "available",
    price: { formatted, currency: "EUR" },
    pricingUnit,
    conflictsWith: [],
    requires: [],
    maxQuantity: null,
    ...overrides,
  };
}

function guaranteedAttribute(code: string, displayName: string): BookingOption {
  return {
    code,
    displayName,
    description: null,
    scope: "room",
    kind: "inventory_requirement",
    guaranteeLevel: "guaranteed",
    priceStatus: "included",
    availabilityStatus: "available",
    price: null,
    pricingUnit: null,
    conflictsWith: [],
    requires: [],
    maxQuantity: null,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// 1 — Berghotel Silberkopf: one category, one rate. The flow should collapse.
// ─────────────────────────────────────────────────────────────────────────────

const silberkopf: HotelBootstrap = {
  hotel: {
    hotelId: "11111111-1111-4111-8111-111111111111",
    name: "Berghotel Silberkopf",
    city: "Oberstdorf",
    countryCode: "DE",
    branding: {
      accentColor: "#1f6feb",
      themeMode: "light",
      logoUrl: null,
      heroImageUrl: null,
    },
  },
  rules: {
    parentBedMaxAge: 2,
    adultFromAge: 18,
    requiresMinorAges: true,
    maxRooms: 3,
    maxNights: 21,
    maxAdultsPerRoom: 3,
  },
  categories: [
    {
      code: "DBL",
      displayName: "Doppelzimmer",
      description: "Alle Zimmer mit Bergblick und Balkon.",
      maxAdults: 2,
      maxOccupancy: 3,
      imageUrl: placeholder("#4a6b57", "#2f4f3e"),
      guaranteedAttributes: [guaranteedAttribute("ROOM_BALCONY", "Balkon")],
    },
  ],
  rates: [
    {
      code: "EX-STD",
      displayName: "Standardrate",
      description: null,
      mealPlanCode: "EX",
      mealPlanLabel: "Ohne Verpflegung",
      includedBenefits: [],
      cancellationSummary: "Kostenfrei stornierbar bis 18:00 Uhr am Anreisetag.",
      paymentSummary: "Zahlung vor Ort.",
    },
  ],
  preferences: [preference("ROOM_QUIET", "Ruhiges Zimmer", "Wir versuchen es einzurichten.")],
  addons: [addon("PARKING", "Parkplatz", null, "9,00 €", "per_night")],
  requirements: [],
  languages: ["de", "en"],
  defaultLanguage: "de",
  currency: "EUR",
};

// ─────────────────────────────────────────────────────────────────────────────
// 2 — Hotel Marea: full meal plan range, sea view sold as a category.
//     Accent is deliberately too light for white text — exercises the WCAG clamp.
// ─────────────────────────────────────────────────────────────────────────────

const marea: HotelBootstrap = {
  hotel: {
    hotelId: "22222222-2222-4222-8222-222222222222",
    name: "Hotel Marea",
    city: "Cala Ratjada",
    countryCode: "ES",
    branding: {
      accentColor: "#ffd400",
      themeMode: "light",
      logoUrl: null,
      heroImageUrl: placeholder("#2e86ab", "#0b3954"),
    },
  },
  rules: {
    parentBedMaxAge: 3,
    adultFromAge: 18,
    requiresMinorAges: true,
    maxRooms: 5,
    maxNights: 28,
    maxAdultsPerRoom: 4,
  },
  categories: [
    {
      code: "STD",
      displayName: "Standardzimmer",
      description: "Blick auf den Innenhof.",
      maxAdults: 2,
      maxOccupancy: 3,
      imageUrl: placeholder("#c9b896", "#8d7a55"),
      guaranteedAttributes: [],
    },
    {
      code: "SEA",
      displayName: "Zimmer mit Meerblick",
      description: "Meerblick ist bei dieser Kategorie garantiert.",
      maxAdults: 2,
      maxOccupancy: 4,
      imageUrl: placeholder("#4fa3c7", "#1d5f80"),
      guaranteedAttributes: [guaranteedAttribute("ROOM_SEA_VIEW", "Meerblick")],
    },
    {
      code: "ACC",
      displayName: "Barrierefreies Zimmer",
      description: "Bodengleiche Dusche, Türbreite 90 cm, Notrufsystem.",
      maxAdults: 2,
      maxOccupancy: 2,
      imageUrl: placeholder("#9aa7b5", "#5d6b7a"),
      guaranteedAttributes: [
        guaranteedAttribute("ROOM_ACCESSIBLE", "Barrierefrei"),
        guaranteedAttribute("ROOM_GROUND_FLOOR", "Erdgeschoss"),
      ],
    },
  ],
  rates: [
    {
      code: "EX-FLEX",
      displayName: "Flexrate ohne Verpflegung",
      description: null,
      mealPlanCode: "EX",
      mealPlanLabel: "Room Only",
      includedBenefits: [],
      cancellationSummary: "Kostenfrei stornierbar bis 24 Stunden vor Anreise.",
      paymentSummary: "Zahlung bei Anreise.",
    },
    {
      code: "BB-FLEX",
      displayName: "Flexrate mit Frühstück",
      description: null,
      mealPlanCode: "BB",
      mealPlanLabel: "Mit Frühstück",
      includedBenefits: ["Frühstücksbuffet"],
      cancellationSummary: "Kostenfrei stornierbar bis 24 Stunden vor Anreise.",
      paymentSummary: "Zahlung bei Anreise.",
    },
    {
      code: "HB-STD",
      displayName: "Halbpension",
      description: null,
      mealPlanCode: "HB",
      mealPlanLabel: "Halbpension",
      includedBenefits: ["Frühstücksbuffet", "Abendessen"],
      cancellationSummary: "Kostenfrei stornierbar bis 7 Tage vor Anreise.",
      paymentSummary: "30 % Anzahlung bei Buchung.",
    },
    {
      code: "FB-STD",
      displayName: "Vollpension",
      description: null,
      mealPlanCode: "FB",
      mealPlanLabel: "Vollpension",
      includedBenefits: ["Frühstücksbuffet", "Mittagessen", "Abendessen"],
      cancellationSummary: "Kostenfrei stornierbar bis 7 Tage vor Anreise.",
      paymentSummary: "30 % Anzahlung bei Buchung.",
    },
    {
      code: "AI-STD",
      displayName: "All Inclusive",
      description: null,
      mealPlanCode: "AI",
      mealPlanLabel: "All Inclusive",
      includedBenefits: ["Alle Mahlzeiten", "Lokale Getränke", "Snacks"],
      cancellationSummary: "Kostenfrei stornierbar bis 14 Tage vor Anreise.",
      paymentSummary: "30 % Anzahlung bei Buchung.",
    },
  ],
  preferences: [
    preference("ROOM_HIGH_FLOOR", "Höhere Etage", "Wunsch — keine Zusage, welche Etage als hoch gilt."),
    preference("ROOM_QUIET", "Ruhige Lage", null),
    preference("ROOM_ADJACENT", "Zimmer nebeneinander", "Wir versuchen, die Zimmer nebeneinander zu legen."),
    preference("ROOM_NEAR_ELEVATOR", "Nahe am Aufzug", null),
  ],
  addons: [
    addon("BREAKFAST", "Frühstück zubuchen", "Auch für einzelne Personen und Tage.", "18,00 €", "per_person_per_night", "booking", {
      conflictsWith: ["BB-FLEX", "HB-STD", "FB-STD", "AI-STD"],
    }),
    addon("PARKING", "Parkplatz", null, "12,00 €", "per_night"),
    addon("CRIB", "Babybett", "Für Kinder bis 3 Jahre.", "0,00 €", "per_stay", "room", {
      priceStatus: "free",
      price: null,
      maxQuantity: 1,
    }),
    addon("EXTRA_BED", "Zustellbett", null, "35,00 €", "per_night", "room", { maxQuantity: 1 }),
    addon("PET", "Haustier", null, "15,00 €", "per_night"),
    addon("LATE_CHECKOUT", "Später Check-out bis 15 Uhr", null, "40,00 €", "per_stay", "booking", {
      guaranteeLevel: "requested",
      availabilityStatus: "limited",
    }),
    addon("SPA", "Spa-Zugang", null, "25,00 €", "per_person_per_night"),
  ],
  requirements: [
    {
      code: "ROOM_ACCESSIBLE",
      displayName: "Barrierefreies Zimmer nötig",
      description: "Wird als feste Anforderung geprüft, nicht als Wunsch.",
      scope: "room",
      kind: "inventory_requirement",
      guaranteeLevel: "guaranteed",
      priceStatus: "included",
      availabilityStatus: "limited",
      price: null,
      pricingUnit: null,
      conflictsWith: [],
      requires: [],
      maxQuantity: null,
    },
    {
      code: "ROOM_CONNECTING",
      displayName: "Verbindungstür",
      description: "Nur zwischen bestimmten Zimmern möglich — wird vor der Zusage geprüft.",
      scope: "room",
      kind: "inventory_requirement",
      guaranteeLevel: "requires_review",
      priceStatus: "included",
      availabilityStatus: "limited",
      price: null,
      pricingUnit: null,
      conflictsWith: [],
      requires: [],
      maxQuantity: null,
    },
  ],
  languages: ["de", "en"],
  defaultLanguage: "de",
  currency: "EUR",
};

// ─────────────────────────────────────────────────────────────────────────────
// 3 — Stadthof Bremen: sea view does not exist, but a courtyard view does — on some rooms
//     only, so it is a wish and must never be labelled as guaranteed.
// ─────────────────────────────────────────────────────────────────────────────

const stadthof: HotelBootstrap = {
  hotel: {
    hotelId: "33333333-3333-4333-8333-333333333333",
    name: "Stadthof Bremen",
    city: "Bremen",
    countryCode: "DE",
    branding: {
      accentColor: "#c2410c",
      themeMode: "dark",
      logoUrl: null,
      heroImageUrl: placeholder("#3f3f46", "#18181b"),
    },
  },
  rules: {
    parentBedMaxAge: null,
    adultFromAge: 16,
    requiresMinorAges: true,
    maxRooms: 4,
    maxNights: 14,
    maxAdultsPerRoom: 2,
  },
  categories: [
    {
      code: "BIZ",
      displayName: "Business Einzelzimmer",
      description: null,
      maxAdults: 1,
      maxOccupancy: 1,
      imageUrl: placeholder("#c2410c", "#7c2d12"),
      guaranteedAttributes: [guaranteedAttribute("ROOM_DESK", "Arbeitsplatz")],
    },
    {
      code: "DBL",
      displayName: "Doppelzimmer",
      description: null,
      maxAdults: 2,
      maxOccupancy: 2,
      imageUrl: placeholder("#6b7280", "#374151"),
      guaranteedAttributes: [],
    },
  ],
  rates: [
    {
      code: "EX-CORP",
      displayName: "Firmenrate",
      description: null,
      mealPlanCode: "EX",
      mealPlanLabel: "Ohne Verpflegung",
      includedBenefits: [],
      cancellationSummary: "Kostenfrei stornierbar bis 18:00 Uhr am Anreisetag.",
      paymentSummary: "Rechnung an die Firma.",
    },
    {
      code: "BB-CORP",
      displayName: "Firmenrate mit Frühstück",
      description: null,
      mealPlanCode: "BB",
      mealPlanLabel: "Mit Frühstück",
      includedBenefits: ["Frühstücksbuffet"],
      cancellationSummary: "Kostenfrei stornierbar bis 18:00 Uhr am Anreisetag.",
      paymentSummary: "Rechnung an die Firma.",
    },
  ],
  preferences: [
    preference("ROOM_COURTYARD", "Blick in den Innenhof", "Nur einzelne Zimmer — wir können es nicht zusagen."),
    preference("ROOM_QUIET", "Ruhige Lage", null),
    preference("ROOM_HIGH_FLOOR", "Höhere Etage", null),
  ],
  addons: [
    addon("PARKING", "Tiefgarage", null, "18,00 €", "per_night"),
    addon("EARLY_CHECKIN", "Früher Check-in ab 10 Uhr", null, "30,00 €", "per_stay", "booking", {
      guaranteeLevel: "requested",
      availabilityStatus: "limited",
    }),
  ],
  requirements: [],
  languages: ["de", "en"],
  defaultLanguage: "de",
  currency: "EUR",
};

export const FIXTURE_HOTELS: Readonly<Record<string, HotelBootstrap>> = {
  pub_silberkopf: silberkopf,
  pub_marea: marea,
  pub_stadthof: stadthof,
};

export const DEFAULT_PUBLIC_KEY = "pub_marea";
